import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FloatLabel } from 'primereact/floatlabel';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';

import Dialog from '../../shared/PFDialog/PFDialogContainer';
import { formAction } from '../../../constants';

import {
  createDashboard,
  cloneDashboard,
  editDashboard,
} from './services/Dashboards.service';
import { CreateDashboardDialogProps } from './interface/CreateDashboardDialog.interface';

const NewCreateDashboardDialog: React.FC<CreateDashboardDialogProps> = ({
  setDialogOpen,
  dialogOpen,
  cloneValues,
  isCloneRef,
  isEditRef,
  fetchData,
}) => {
  const [loading, setLoading] = React.useState(false);
  const dialogSettings = {
    title: (
      <div className="flex align-items-start flex-column">
        {isEditRef?.current ? 'Edit Dashboard' : 'Create Dashboard'}
      </div>
    ),
    button1Text: '',
    button2Text: isEditRef?.current ? 'edit' : 'Create',
    showButton1: true,
    showButton2: true,
  };

  const history = useHistory();
  // To show the message
  const toast = useRef<Toast>(null);

  // **** Form Validation Schema ****
  const CreateDashboardSchema = Yup.object().shape({
    name: Yup.string().trim().required('Required'),
    description: Yup.string().trim().required('Required'),
  });

  const buttonLabel = dialogSettings.button2Text
    ? dialogSettings.button2Text
    : 'Save';

  React.useEffect(() => {
    toast?.current?.clear();
  }, [dialogOpen]);

  const getName = () => {
    let name = null;
    if (isCloneRef.current) {
      name = `${cloneValues?.name} Copy`;
    } else if (isEditRef.current) {
      name = `${cloneValues?.name}`;
    }
    return name;
  };

  const getDescription = () => {
    let description = null;
    if (isCloneRef.current) {
      description = `${cloneValues?.description} Copy`;
    } else if (isEditRef.current) {
      description = `${cloneValues?.description}`;
    }
    return description;
  };

  const createDashboardFormik = useFormik({
    initialValues: {
      name: getName(),
      description: getDescription(),
      private:
        isCloneRef.current || isEditRef.current ? cloneValues?.private : 0,
    },
    onSubmit: async values => {
      if (isCloneRef.current) {
        setLoading(true);
        const result = await cloneDashboard(cloneValues?.dashboard_id, {
          ...values,
          name: values?.name ? values?.name.trim() : null,
          description: values?.description ? values?.description.trim() : null,
        });
        if (!result?.error) {
          fetchData();
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: result?.message,
          });
          setDialogOpen(false);

          isCloneRef.current = false;
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: result.message,
            life: 3000,
          });
        }
        setLoading(false);
      } else if (isEditRef.current) {
        setLoading(true);
        const result = await editDashboard(cloneValues?.dashboard_id, values);
        if (!result?.error) {
          fetchData();
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: result?.message,
            life: 3000,
          });
          setDialogOpen(false);

          isEditRef.current = false;
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: result.message,
            life: 3000,
          });
        }
        setLoading(false);
      } else {
        setLoading(true);
        const result = await createDashboard({
          ...values,
          name: values?.name ? values?.name.trim() : null,
          description: values?.description ? values?.description.trim() : null,
        });
        if (!result.error) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: result.message,
            life: 3000,
          });
          history.push(`/admin/dashboard/edit/${result.dashboardId}`);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: result.message,
            life: 3000,
          });
        }
        setLoading(false);
      }
    },
    validationSchema: CreateDashboardSchema,
    enableReinitialize: true,
  });

  const isSubmitDisabled =
    loading ||
    (!isCloneRef.current &&
      (!createDashboardFormik.dirty || !createDashboardFormik.isValid));
  return (
    <Dialog
      header={dialogSettings?.title}
      visible={dialogOpen}
      className="w-11 lg:w-5 xl:w-4"
      onHide={() => {
        setDialogOpen(false);
        isCloneRef.current = false;
        isEditRef.current = false;
        toast?.current?.clear();
        createDashboardFormik.resetForm();
      }}
      draggable={false}
      footer={
        <div className="flex justify-content-end gap-1">
          <Button
            label={
              (dialogSettings.button1Text && dialogSettings.button1Text) ||
              'Cancel'
            }
            className="p-button-text"
            outlined
            onClick={() => {
              setDialogOpen(false);
              isCloneRef.current = false;
              isEditRef.current = false;
              toast?.current?.clear();
              createDashboardFormik.resetForm();
            }}
            size="small"
          />
          {dialogSettings.button2Text === formAction.EDIT ? (
            <Button
              label={buttonLabel}
              disabled={isSubmitDisabled}
              onClick={() => createDashboardFormik.handleSubmit()}
              size="small"
              icon="pi pi-plus"
              loading={loading}
            />
          ) : (
            <Button
              label={buttonLabel}
              disabled={isSubmitDisabled}
              onClick={() => createDashboardFormik.handleSubmit()}
              size="small"
              icon="pi pi-plus"
              loading={loading}
              pt={{ root: { className: 'lg:w-3 mr-0' } }}
            />
          )}
        </div>
      }
    >
      <div className="flex flex-column gap-3 pt-2">
        <Toast ref={toast} />
        <div>
          <FloatLabel>
            <InputText
              id="name"
              value={createDashboardFormik?.values?.name || ''}
              onChange={createDashboardFormik.handleChange}
              required
              className="w-full height-40"
            />
            <label htmlFor="name">
              Name<span className="text-red-400"> *</span>
            </label>
          </FloatLabel>
        </div>
        <div>
          <FloatLabel>
            <InputText
              id="description"
              value={createDashboardFormik?.values?.description || ''}
              onChange={createDashboardFormik.handleChange}
              required
              className="height-40 w-full"
            />
            <label htmlFor="name">
              Description<span className="text-red-400"> *</span>
            </label>
          </FloatLabel>
        </div>
        <div>
          <div className="flex align-items-center">
            <Checkbox
              inputId="private"
              onChange={e => {
                createDashboardFormik.setFieldValue(
                  'private',
                  e.checked ? 1 : 0
                );
              }}
              checked={
                createDashboardFormik?.values?.private === 1 ? true : false
              }
            />
            <label htmlFor="private" className="ml-2">
              Private
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default NewCreateDashboardDialog;
